<script lang="ts">
  import { onMount } from "svelte";
  import { getTokenStorage } from "../utils/localStorage";
  import { appStore } from "../stores/appStore";
  import Navbar from "../components/Navbar.svelte";
  // import type { AppApiClient } from '../api_contracts/app.client';
  import { AppApiClient } from "../api_contracts/app.client";
  import { UserProfile } from "../api_contracts/app";

  // Form state
  let owner = $state("");
  let profile: UserProfile | null = $state(null);
  let repositoryName = $state("");
  let description = $state("");
  let visibility = $state("public");
  let template = $state("none");
  let addReadme = $state(false);
  let gitignore = $state("none");
  let license = $state("none");

  // Repository name validation
  let isCheckingName = $state(false);
  let nameAvailable = $state<boolean | null>(null);
  let nameCheckMessage = $state("");
  let checkNameTimeout: ReturnType<typeof setTimeout> | null = null;

  // Client and token state
  let client: AppApiClient | null = $state(null);
  let token: string = $state("");
  let loading: boolean = $state(false);
  let error: string = $state("");
  let fetchingProfile: boolean = $state(true);

  // Subscribe to store
  appStore.subscribe((state) => {
    profile = state.profile;
    client = state.client;
    token = state.token;
  });

  // Character counter for description
  let descriptionLength = $derived(description.length);
  const maxDescriptionLength = 136;

  // Form validation
  let isAuthenticated = $state(false);

  $effect(() => {
    const storedToken = getTokenStorage();
    isAuthenticated = !!storedToken;
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      window.location.hash = "/login";
    }
  });

  // Fetch user profile to get username
  async function fetchUserProfile() {
    if (!client || !token) {
      fetchingProfile = false;
      return;
    }

    try {
      const result = await client.getUserProfile({ token });

      if (result.response.response.successFull) {
        profile = result.response.profile;
        owner = result.response.profile?.userName || "";
      } else {
        console.error(
          "Failed to fetch user profile:",
          result.response.response.reason,
        );
        owner = "username"; // Fallback
      }
    } catch (e: any) {
      console.error("Error fetching user profile:", e);
      owner = "username"; // Fallback
    } finally {
      fetchingProfile = false;
    }
  }

  // Check if repository name is available
  async function checkRepositoryNameAvailability(name: string) {
    if (!client || !owner || !name.trim()) {
      nameAvailable = null;
      nameCheckMessage = "";
      return;
    }

    const sanitizedName = name.trim().replace(/\s+/g, '-');
    if (!sanitizedName) {
      nameAvailable = null;
      nameCheckMessage = "";
      return;
    }

    try {
      isCheckingName = true;
      const result = await client.getRepositoryDetails({
        token: token || "",
        ownerId: owner,
        repositoryName: sanitizedName,
      });

      // If the repository is found, name is not available
      if (result.response.successFull && result.response.repository) {
        nameAvailable = false;
        nameCheckMessage = `Repository "${sanitizedName}" already exists`;
      } else {
        // Repository not found, name is available
        nameAvailable = true;
        nameCheckMessage = `"${sanitizedName}" is available`;
      }
    } catch (e: any) {
      // On error, assume the repository doesn't exist (name is available)
      nameAvailable = true;
      nameCheckMessage = `"${sanitizedName}" is available`;
    } finally {
      isCheckingName = false;
    }
  }

  // Watch for repository name changes and check availability with debouncing
  $effect(() => {
    if (repositoryName && owner) {
      // Clear previous timeout
      if (checkNameTimeout) {
        clearTimeout(checkNameTimeout);
      }

      // Reset validation state immediately
      nameAvailable = null;
      nameCheckMessage = "";

      // Set new timeout for debounced check
      checkNameTimeout = setTimeout(() => {
        checkRepositoryNameAvailability(repositoryName);
      }, 500); // 500ms debounce
    }

    // Cleanup function
    return () => {
      if (checkNameTimeout) {
        clearTimeout(checkNameTimeout);
      }
    };
  });

  onMount(() => {
    const storedToken = getTokenStorage();
    if (storedToken) {
		console.log("Stored token found, fetching profile...");
      if (!profile) {
		 console.log("Fetching user profile...");
        fetchUserProfile();
      }else{
        owner = profile.userName || "";
        fetchingProfile=false;
      }
    } else {
      fetchingProfile = false;
    }
  });

  // Generate random repository name suggestion
  const adjectives = [
    "automatic",
    "incredible",
    "amazing",
    "fantastic",
    "cool",
    "super",
  ];
  const nouns = ["meme", "project", "app", "tool", "platform", "system"];
  const randomSuggestion = `${adjectives[Math.floor(Math.random() * adjectives.length)]}-${nouns[Math.floor(Math.random() * nouns.length)]}`;

  function useSuggestion() {
    repositoryName = randomSuggestion;
  }

  async function handleSubmit(event: Event) {
    event.preventDefault();

    // Validate required fields
    if (!repositoryName.trim()) {
      alert("Please enter a repository name");
      return;
    }

      const sanitizedRepoName = repositoryName.trim().replace(/\s+/g, '-');

      if (!sanitizedRepoName) {
      alert("Please enter a repository name");
      return;
    }

    if (!client) {
      error = "gRPC client not initialized";
      alert("Error: gRPC client not initialized. Please refresh the page.");
      return;
    }

    if (!token) {
      error = "No authentication token";
      alert("Error: Not authenticated. Please login again.");
      window.location.hash = "/login";
      return;
    }

    try {
      loading = true;
      error = "";

      const createRepositoryRequest = {
        token,
        owner,
        repositoryName : sanitizedRepoName,
        description,
        visibility,
        template,
        addReadme,
        gitignore,
        license,
      };

      const result = await client.createRepository(createRepositoryRequest);

      if (result.response.successFull) {
        console.log("Repository created successfully:", result.response);
        // Redirect to repository page after creation
        window.location.hash = `/users/${profile.userName}/repositories/${sanitizedRepoName}`;
      } else {
        error = result.response.reason || "Failed to create repository";
        alert(`Error: ${error}`);
      }
    } catch (e: any) {
      error = e.message || "Error creating repository";
      console.error("Error:", e);
      alert(`Error creating repository: ${error}`);
    } finally {
      loading = false;
    }
  }

  
</script>

<Navbar />

<div
  class="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-200"
>
  <div class="container mx-auto px-4 py-8 max-w-4xl">
    <h1 class="text-2xl font-normal mb-1 text-gray-900 dark:text-gray-100">
      Create a new repository
    </h1>
    <p class="text-gray-600 dark:text-gray-400 text-sm mb-6">
      Repositories contain a project's files and version history. Have a project
      elsewhere?
      <a
        href="#/import"
        class="text-blue-500 dark:text-blue-400 hover:underline"
        >Import a repository.</a
      >
      <br />
      Required fields are marked with an asterisk (*).
    </p>

    <form class="space-y-8" onsubmit={handleSubmit}>
      <!-- Section 1: General -->
      <div>
        <h2 class="text-xl font-normal mb-4 text-gray-900 dark:text-gray-100">
          <span class="text-gray-400 dark:text-gray-500 mr-2">1</span> General
        </h2>

        <div class="space-y-4">
          <!-- Owner -->
          <div class="flex flex-col md:flex-row md:items-center">
            <label
              for="owner"
              class="text-sm font-medium w-full md:w-1/4 mb-1 md:mb-0"
            >
              Owner <span class="text-red-500">*</span>
            </label>
            <div
              class="w-full md:w-3/4 flex items-center p-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-800"
            >
              {#if fetchingProfile}
                <div
                  class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"
                ></div>
                <span class="text-gray-500 dark:text-gray-400">Loading...</span>
              {:else}
                <div
                  class="w-5 h-5 rounded-full mr-2 bg-indigo-600 flex items-center justify-center text-white text-xs font-semibold"
                >
                  {owner ? owner.charAt(0).toUpperCase() : "U"}
                </div>
                <span class="text-gray-900 dark:text-gray-200 font-medium"
                  >{owner || "username"}</span
                >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4 ml-auto text-gray-500 dark:text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              {/if}
            </div>
          </div>

          <!-- Repository Name -->
          <div class="flex flex-col md:flex-row md:items-start">
            <label
              for="repo-name"
              class="text-sm font-medium w-full md:w-1/4 mb-1 md:mb-0 pt-2"
            >
              Repository name <span class="text-red-500">*</span>
            </label>
            <div class="w-full md:w-3/4">
              <div class="relative">
                <input
                  type="text"
                  id="repo-name"
                  bind:value={repositoryName}
                  class="w-full px-3 py-2 border {nameAvailable === false ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : nameAvailable === true ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : 'border-gray-300 dark:border-gray-700 focus:border-blue-500 focus:ring-blue-500'} rounded-md bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-colors"
                  placeholder="e.g. my-awesome-project"
                  maxlength="25"
                  required
                />
                {#if isCheckingName}
                  <div class="absolute right-3 top-1/2 -translate-y-1/2">
                    <div class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 dark:border-gray-400"></div>
                  </div>
                {/if}
              </div>
              {#if nameCheckMessage}
                <p class="text-xs mt-1 {nameAvailable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}">
                  {nameCheckMessage}
                </p>
              {:else}
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Great repository names are short and memorable. How about
                  <button
                    type="button"
                    onclick={useSuggestion}
                    class="text-blue-500 dark:text-blue-400 hover:underline"
                  >
                    {randomSuggestion}
                  </button>
                </p>
              {/if}
            </div>
          </div>

          <!-- Description -->
          <div class="flex flex-col md:flex-row">
            <label
              for="description"
              class="text-sm font-medium w-full md:w-1/4 mb-1 md:mb-0 pt-2"
            >
              Description <span class="text-gray-500">(optional)</span>
            </label>
            <textarea
              id="description"
              rows="3"
              bind:value={description}
              maxlength={maxDescriptionLength}
              class="w-full md:w-3/4 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors resize-y"
              placeholder="A short description of your repository"
            ></textarea>
          </div>
          <p
            class="text-xs text-gray-600 dark:text-gray-400 text-right md:text-left ml-0 md:ml-[25%] -mt-2"
          >
            {descriptionLength} / {maxDescriptionLength} characters
          </p>
        </div>
      </div>

      <hr class="border-gray-300 dark:border-gray-700" />

      <!-- Section 2: Configuration -->
      <div>
        <h2 class="text-xl font-normal mb-4 text-gray-900 dark:text-gray-100">
          <span class="text-gray-400 dark:text-gray-500 mr-2">2</span> Configuration
        </h2>

        <div class="space-y-6">
          <!-- Visibility -->
          <div
            class="flex flex-col md:flex-row justify-between items-start md:items-center"
          >
            <div>
              <label for="visibility" class="block text-sm font-medium mb-1">
                Choose visibility <span class="text-red-500">*</span>
              </label>
              <p class="text-xs text-gray-600 dark:text-gray-400">
                Choose who can see and commit to this repository
              </p>
            </div>
            <div class="relative mt-2 md:mt-0 w-full md:w-auto">
              <select
                id="visibility"
                bind:value={visibility}
                class="min-w-[150px] px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors appearance-none"
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-gray-400"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>

          <!-- Template -->
          <div
            class="flex flex-col md:flex-row justify-between items-start md:items-center pt-4 border-t border-gray-300 dark:border-gray-700"
          >
            <div>
              <label class="block text-sm font-medium mb-1"
                >Start with a template</label
              >
              <p class="text-xs text-gray-600 dark:text-gray-400">
                Templates help you configure your repository with files.
              </p>
            </div>
            <div class="relative mt-2 md:mt-0 w-full md:w-auto">
              <select
                bind:value={template}
                class="min-w-[150px] px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors appearance-none"
              >
                <option value="none">No template</option>
                <option value="python">Python</option>
                <option value="node">Node.js</option>
                <option value="go">Go</option>
                <option value="rust">Rust</option>
                <option value="java">Java</option>
                <option value="cpp">C++</option>
                <option value="csharp">C#</option>
                <option value="ruby">Ruby</option>
                <option value="php">PHP</option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-gray-400"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>

          <!-- Add README -->
          <div
            class="flex flex-col md:flex-row justify-between items-start md:items-center pt-4 border-t border-gray-300 dark:border-gray-700"
          >
            <div>
              <label class="block text-sm font-medium mb-1">Add README</label>
              <p class="text-xs text-gray-600 dark:text-gray-400">
                READMEs can be longer descriptions.
                <a
                  href="#"
                  class="text-blue-500 dark:text-blue-400 hover:underline"
                  >About READMEs</a
                >
              </p>
            </div>
            <label
              class="relative inline-block w-10 h-5 mt-2 md:mt-0 cursor-pointer"
            >
              <input
                type="checkbox"
                bind:checked={addReadme}
                class="sr-only peer"
              />
              <span
                class="absolute inset-0 bg-gray-300 dark:bg-gray-700 rounded-full transition-colors peer-checked:bg-green-600"
              ></span>
              <span
                class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"
              ></span>
            </label>
          </div>

          <!-- Add .gitignore -->
          <div
            class="flex flex-col md:flex-row justify-between items-start md:items-center pt-4 border-t border-gray-300 dark:border-gray-700"
          >
            <div>
              <label class="block text-sm font-medium mb-1"
                >Add .gitignore</label
              >
              <p class="text-xs text-gray-600 dark:text-gray-400">
                .gitignore tells git which files not to track.
                <a
                  href="#"
                  class="text-blue-500 dark:text-blue-400 hover:underline"
                  >About ignoring files</a
                >
              </p>
            </div>
            <div class="relative mt-2 md:mt-0 w-full md:w-auto">
              <select
                bind:value={gitignore}
                class="min-w-[150px] px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors appearance-none"
              >
                <option value="none">No .gitignore</option>
                <option value="node">Node.js</option>
                <option value="python">Python</option>
                <option value="go">Go</option>
                <option value="rust">Rust</option>
                <option value="java">Java</option>
                <option value="cpp">C++</option>
                <option value="csharp">C#</option>
                <option value="ruby">Ruby</option>
                <option value="php">PHP</option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-gray-400"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>

          <!-- Add license -->
          <div
            class="flex flex-col md:flex-row justify-between items-start md:items-center pt-4 border-t border-gray-300 dark:border-gray-700"
          >
            <div>
              <label class="block text-sm font-medium mb-1">Add license</label>
              <p class="text-xs text-gray-600 dark:text-gray-400">
                Licenses explain how others can use your code.
                <a
                  href="#"
                  class="text-blue-500 dark:text-blue-400 hover:underline"
                  >About licenses</a
                >
              </p>
            </div>
            <div class="relative mt-2 md:mt-0 w-full md:w-auto">
              <select
                bind:value={license}
                class="min-w-[150px] px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors appearance-none"
              >
                <option value="none">No license</option>
                <option value="mit">MIT License</option>
                <option value="apache-2.0">Apache License 2.0</option>
                <option value="gpl-3.0">GNU GPL v3.0</option>
                <option value="bsd-3-clause"
                  >BSD 3-Clause "New" or "Revised" License</option
                >
                <option value="bsd-2-clause"
                  >BSD 2-Clause "Simplified" License</option
                >
                <option value="lgpl-3.0">GNU LGPL v3.0</option>
                <option value="agpl-3.0">GNU AGPL v3.0</option>
                <option value="mpl-2.0">Mozilla Public License 2.0</option>
                <option value="unlicense">The Unlicense</option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-gray-400"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="pt-6 border-t border-gray-300 dark:border-gray-700">
        <button
          type="submit"
          disabled={loading || isCheckingName || nameAvailable === false}
          class="py-2 px-4 rounded-md bg-green-600 hover:bg-green-700 text-white font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? "Creating repository..." : "Create repository"}
        </button>
        {#if nameAvailable === false && !isCheckingName}
          <p class="text-xs text-red-600 dark:text-red-400 mt-2">
            Please choose a different repository name
          </p>
        {/if}
      </div>
    </form>
  </div>
</div>
